import logger from './logger';
import toast from 'react-hot-toast';

/**
 * Gestion centralisée des erreurs
 */

/**
 * Types d'erreurs courantes
 */
export const ErrorTypes = {
  NETWORK: 'NETWORK_ERROR',
  AUTH: 'AUTH_ERROR',
  VALIDATION: 'VALIDATION_ERROR',
  PERMISSION: 'PERMISSION_ERROR',
  NOT_FOUND: 'NOT_FOUND',
  SERVER: 'SERVER_ERROR',
  UNKNOWN: 'UNKNOWN_ERROR'
};

/**
 * Messages d'erreur user-friendly en français et anglais
 */
const errorMessages = {
  fr: {
    [ErrorTypes.NETWORK]: 'Problème de connexion. Vérifiez votre internet et réessayez.',
    [ErrorTypes.AUTH]: 'Vous devez être connecté pour effectuer cette action.',
    [ErrorTypes.VALIDATION]: 'Les informations saisies ne sont pas valides. Veuillez vérifier.',
    [ErrorTypes.PERMISSION]: "Vous n'avez pas la permission d'effectuer cette action.",
    [ErrorTypes.NOT_FOUND]: "L'élément recherché n'existe pas ou a été supprimé.",
    [ErrorTypes.SERVER]: 'Une erreur serveur est survenue. Veuillez réessayer plus tard.',
    [ErrorTypes.UNKNOWN]: 'Une erreur inattendue est survenue. Veuillez réessayer.'
  },
  en: {
    [ErrorTypes.NETWORK]: 'Connection problem. Check your internet and try again.',
    [ErrorTypes.AUTH]: 'You must be logged in to perform this action.',
    [ErrorTypes.VALIDATION]: 'The entered information is not valid. Please check.',
    [ErrorTypes.PERMISSION]: "You don't have permission to perform this action.",
    [ErrorTypes.NOT_FOUND]: 'The requested item does not exist or has been deleted.',
    [ErrorTypes.SERVER]: 'A server error occurred. Please try again later.',
    [ErrorTypes.UNKNOWN]: 'An unexpected error occurred. Please try again.'
  }
};

/**
 * Détecter le type d'erreur à partir d'une erreur
 */
const detectErrorType = (error) => {
  if (!error) return ErrorTypes.UNKNOWN;

  const errorMessage = error.message?.toLowerCase() || '';
  const errorCode = error.code || '';

  // Erreurs réseau (incluant QUIC et autres erreurs de protocole)
  if (
    errorMessage.includes('network') ||
    errorMessage.includes('fetch') ||
    errorMessage.includes('connection') ||
    errorMessage.includes('quic') ||
    errorMessage.includes('protocol') ||
    errorMessage.includes('retransmission') ||
    errorMessage.includes('too many') ||
    errorCode === 'unavailable' ||
    errorCode === 'deadline-exceeded' ||
    errorCode === 'aborted' ||
    errorCode === 'internal'
  ) {
    return ErrorTypes.NETWORK;
  }

  // Erreurs d'authentification
  if (
    errorMessage.includes('auth') ||
    errorMessage.includes('permission-denied') ||
    errorCode === 'permission-denied' ||
    errorCode === 'unauthenticated'
  ) {
    return ErrorTypes.AUTH;
  }

  // Erreurs de validation
  if (
    errorMessage.includes('invalid') ||
    errorMessage.includes('validation') ||
    errorCode === 'invalid-argument'
  ) {
    return ErrorTypes.VALIDATION;
  }

  // Erreurs de permission
  if (
    errorMessage.includes('permission') ||
    errorCode === 'permission-denied'
  ) {
    return ErrorTypes.PERMISSION;
  }

  // Erreurs 404
  if (
    errorMessage.includes('not found') ||
    errorCode === 'not-found' ||
    errorCode === 404
  ) {
    return ErrorTypes.NOT_FOUND;
  }

  // Erreurs serveur
  if (
    errorMessage.includes('server') ||
    errorCode === 'internal' ||
    errorCode === 500
  ) {
    return ErrorTypes.SERVER;
  }

  return ErrorTypes.UNKNOWN;
};

/**
 * Gérer une erreur de manière centralisée
 * 
 * @param {Error} error - L'erreur à gérer
 * @param {Object} options - Options de gestion
 * @param {string} options.language - Langue pour les messages (fr/en)
 * @param {boolean} options.showToast - Afficher un toast (défaut: true)
 * @param {Function} options.onError - Callback personnalisé
 * @param {Object} options.context - Contexte supplémentaire pour le logging
 */
export const handleError = (error, options = {}) => {
  const {
    language = 'fr',
    showToast = true,
    onError,
    context = {}
  } = options;

  const errorType = detectErrorType(error);
  const userMessage = errorMessages[language]?.[errorType] || errorMessages.fr[ErrorTypes.UNKNOWN];

  // Logger l'erreur
  logger.error(error, {
    type: errorType,
    ...context
  });

  // Afficher un toast user-friendly
  if (showToast) {
    toast.error(userMessage, {
      duration: 4000,
      position: 'top-center'
    });
  }

  // Callback personnalisé si fourni
  if (onError && typeof onError === 'function') {
    onError(error, errorType, userMessage);
  }

  return {
    type: errorType,
    message: userMessage,
    originalError: error
  };
};

/**
 * Wrapper pour les fonctions async avec gestion d'erreur automatique
 * 
 * @param {Function} asyncFn - Fonction async à wrapper
 * @param {Object} options - Options de gestion d'erreur
 */
export const withErrorHandling = (asyncFn, options = {}) => {
  return async (...args) => {
    try {
      return await asyncFn(...args);
    } catch (error) {
      handleError(error, options);
      throw error; // Re-throw pour permettre la gestion en amont si nécessaire
    }
  };
};

/**
 * Créer un handler d'erreur pour un composant React
 */
export const createErrorHandler = (language = 'fr') => {
  return (error, context = {}) => {
    return handleError(error, {
      language,
      context
    });
  };
};

const errorHandler = {
  handleError,
  withErrorHandling,
  createErrorHandler,
  ErrorTypes
};

export default errorHandler;